/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useState } from "react"
import { observer } from "mobx-react-lite"
import { Form, Formik } from "formik"
import * as Yup from "yup"
import { LoadingButton } from "@mui/lab"
import Alert, { Mode } from "@medaica/common/components/alert"
import TextInput from "@medaica/common/components/forms/text-input"
import { logError } from "@medaica/common/services/util"
import { useBookingStores } from "../booking-context"
import BookingLayout from "../components/booking-layout"
import bookingCopy from "../bookingCopy"
import { bookingColors } from "../booking-theme"

const copy = bookingCopy.emailEntry

const validationSchema = Yup.object({
  email: Yup.string().trim().email(bookingCopy.patient.errors.email).required(bookingCopy.patient.errors.required),
})

/**
 * Bare `/schedule` entry (D29). Submitting always advances to the code gate with the
 * neutral "if you have exams with us…" copy — the response never reveals whether
 * the email is a known buyer (08 §1).
 */
const EmailEntry = observer((): ReactElement => {
  const { sessionStore } = useBookingStores()
  const [error, setError] = useState<string | null>(null)

  return (
    <BookingLayout>
      <div className="max-w-md mx-auto mt-16 bg-white rounded-lg shadow-sm px-8 py-10">
        <h1 className="text-2xl font-bold text-center" style={{ color: bookingColors.primary }}>
          {copy.title}
        </h1>
        <p className="mt-2 mb-6 text-center text-gray-600">{copy.subtitle}</p>

        {error ? (
          <Alert className="mb-4" mode={Mode.error}>
            {error}
          </Alert>
        ) : null}

        <Formik
          initialValues={{ email: "" }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            setError(null)
            try {
              await sessionStore.startSession({ email: values.email.trim() })
              // startSession navigates to the code gate (neutral copy — D29)
            } catch (submitError) {
              logError(submitError)
              setError(copy.error)
            } finally {
              setSubmitting(false)
            }
          }}
        >
          {({ errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <TextInput
                label={copy.emailLabel}
                name="email"
                type="email"
                errors={errors}
                touched={touched}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <LoadingButton
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3 }}
                type="submit"
                loading={isSubmitting}
              >
                {copy.submit}
              </LoadingButton>
            </Form>
          )}
        </Formik>

        <p className="mt-4 text-center text-sm text-gray-500">{copy.neutralNote}</p>
      </div>
    </BookingLayout>
  )
})

export default EmailEntry
